// Authentication utility functions

/**
 * Safely parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {any} defaultValue - default value if parsing fails
 * @returns {any} parsed value or default
 */
export const getStorageItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    if (!item || item === "undefined" || item === "null") {
      return defaultValue
    }
    return JSON.parse(item)
  } catch (error) {
    console.warn(`Error parsing localStorage key "${key}":`, error)
    return defaultValue
  }
}

/**
 * Safely set item in localStorage
 * @param {string} key - localStorage key
 * @param {any} value - value to store
 */
export const setStorageItem = (key, value) => {
  try {
    if (value === undefined || value === null) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(value))
    }
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error)
  }
}

/**
 * Remove item from localStorage
 * @param {string} key - localStorage key
 */
export const removeStorageItem = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error)
  }
}

/**
 * Get authentication token
 * @returns {string|null} token or null
 */
export const getAuthToken = () => {
  try {
    const token = localStorage.getItem("admin_token")
    return token && token !== "undefined" && token !== "null" ? token : null
  } catch (error) {
    console.error("Error getting auth token:", error)
    return null
  }
}

/**
 * Set authentication token
 * @param {string} token - JWT token
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("admin_token", token)
  } else {
    localStorage.removeItem("admin_token")
  }
}

/**
 * Get admin info
 * @returns {object|null} admin info or null
 */
export const getAdminInfo = () => {
  return getStorageItem("admin_info", null)
}

/**
 * Set admin info
 * @param {object} adminData - admin information
 */
export const setAdminInfo = (adminData) => {
  setStorageItem("admin_info", adminData)
}

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  removeStorageItem("admin_token")
  removeStorageItem("admin_info")
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = getAuthToken()
  return !!token
}
